import { classNames } from '@fullstory/util/fmt';
import React, { forwardRef, HTMLAttributes, PropsWithChildren } from 'react';
import { DisplayProps, getDisplayClassNames } from '../../styled-system/domains/display';
import {
  FlexContainerProps,
  getFlexContainerClassNames,
} from '../../styled-system/domains/flex-container';
import { SpaceScale } from '../../styled-system/types/scale';
import { FSMaskProp } from '../../types';
import { identifyComponent } from '../../util';
import css, { ClassNames } from './index.module.scss';

type FlexScale = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
type OverflowTypes = 'scroll' | 'hidden' | 'auto';

/**
 * The box model for space scale applies values in the order of normal css box styling:
 * [top, right, bottom, left]
 **/
export type BoxStyleSpaceScale =
  | [SpaceScale, SpaceScale, SpaceScale, SpaceScale]
  | [SpaceScale, SpaceScale];

interface BoxBaseProps
  extends HTMLAttributes<HTMLDivElement>,
    Pick<React.CSSProperties, 'maxWidth' | 'minWidth' | 'maxHeight' | 'minHeight'>,
    FlexContainerProps,
    DisplayProps {
  testId?: string;
  className?: string;
  alignSelf?: 'auto' | 'flex-start' | 'flex-end' | 'center' | 'baseline' | 'stretch';
  order?: FlexScale;
  /**
   * Can be a single value for equal spacing, or an array [top, right, bottom, left]
   * Uses our spacing scale: 0 | 4 | 8 | 12 | 16 | 20 | 24 | 32 | 40 | 48 | 56 | 64
   * */
  margin?: SpaceScale | BoxStyleSpaceScale;
  /**
   * Can be a single value for equal spacing, or an array [top, right, bottom, left]
   * Uses our spacing scale: 0 | 4 | 8 | 12 | 16 | 20 | 24 | 32 | 40 | 48 | 56 | 64
   * */
  padding?: SpaceScale | BoxStyleSpaceScale;
  /** Uses our spacing scale: 0 | 4 | 8 | 12 | 16 | 20 | 24 | 32 | 40 | 48 | 56 | 64 */
  marginBottom?: SpaceScale;
  /** Uses our spacing scale: 0 | 4 | 8 | 12 | 16 | 20 | 24 | 32 | 40 | 48 | 56 | 64 */
  marginTop?: SpaceScale;
  /** Uses our spacing scale: 0 | 4 | 8 | 12 | 16 | 20 | 24 | 32 | 40 | 48 | 56 | 64 */
  marginRight?: SpaceScale;
  /** Uses our spacing scale: 0 | 4 | 8 | 12 | 16 | 20 | 24 | 32 | 40 | 48 | 56 | 64 */
  marginLeft?: SpaceScale;
  /** Uses our spacing scale: 0 | 4 | 8 | 12 | 16 | 20 | 24 | 32 | 40 | 48 | 56 | 64 */
  paddingBottom?: SpaceScale;
  /** Uses our spacing scale: 0 | 4 | 8 | 12 | 16 | 20 | 24 | 32 | 40 | 48 | 56 | 64 */
  paddingTop?: SpaceScale;
  /** Uses our spacing scale: 0 | 4 | 8 | 12 | 16 | 20 | 24 | 32 | 40 | 48 | 56 | 64 */
  paddingRight?: SpaceScale;
  /** Uses our spacing scale: 0 | 4 | 8 | 12 | 16 | 20 | 24 | 32 | 40 | 48 | 56 | 64 */
  paddingLeft?: SpaceScale;
  /**
   * Whether the box should be displayed inline. Overrides `display` value.
   * TODO(jcarroll2007): remove in favor of `display="inline"`
   */
  inline?: boolean;
  /** Allows this box to collapse to a size smaller than its contents as a flex child */
  collapsible?: boolean;
  overflow?: OverflowTypes;
  overflowX?: OverflowTypes;
  overflowY?: OverflowTypes;
  fsMask?: FSMaskProp;
}

export interface BoxWidthProps extends BoxBaseProps {
  width?: number | string;
  height?: number | string;
  flex?: never;
  flexGrow?: never;
  flexShrink?: never;
  flexBasis?: never;
}

export interface BoxFlexLonghandProps extends BoxBaseProps {
  flexGrow?: FlexScale;
  flexShrink?: FlexScale;
  flexBasis?: number | 'auto' | 'inherit' | 'initial' | 'unset';
  flex?: never;
  width?: never;
  height?: never;
}

export interface BoxFlexShorthandProps extends BoxBaseProps {
  flex?: FlexScale | 'initial' | 'auto' | 'none';
  flexGrow?: never;
  flexShrink?: never;
  flexBasis?: never;
  width?: never;
  height?: never;
}

export type BoxProps = BoxWidthProps | BoxFlexShorthandProps | BoxFlexLonghandProps;

export const Box = forwardRef<HTMLDivElement, PropsWithChildren<BoxProps>>(
  (
    {
      testId = 'box',
      className,
      width,
      height,
      flex,
      flexBasis,
      flexGrow,
      flexShrink,
      alignSelf,
      overflow,
      overflowX,
      overflowY,
      order,
      margin,
      padding,
      marginTop,
      marginBottom,
      marginLeft,
      marginRight,
      paddingTop,
      paddingBottom,
      paddingLeft,
      paddingRight,
      inline = false,
      collapsible = false,
      maxWidth,
      minWidth,
      maxHeight,
      minHeight,
      style,
      fsMask = null,

      /**
       * Flex Container Props
       */
      justifyContent,
      alignItems,
      alignContent,
      flexDirection,
      flexWrap,
      gap,
      rowGap,
      columnGap,

      /**
       * Display Props
       */
      display,
      ...restOfProps
    },
    ref,
  ) => (
    <div
      data-component="Box"
      data-testid={testId}
      {...restOfProps}
      {...identifyComponent('box', restOfProps)}
      className={classNames(
        'fs_component_box',
        fsMask,
        className,
        css.boxComponent,
        ...generateSpaceClasses('p', padding),
        ...generateSpaceClasses('m', margin),
        ...generateSpaceClasses('p', [paddingTop, paddingRight, paddingBottom, paddingLeft]),
        ...generateSpaceClasses('m', [marginTop, marginRight, marginBottom, marginLeft]),
        {
          [getCssClassName(`flex_${flex}`)]: flex != null,
          [getCssClassName(`flexGrow_${flexGrow}`)]: flexGrow != null,
          [getCssClassName(`flexShrink_${flexShrink}`)]: flexShrink != null,
          [getCssClassName(`order_${order}`)]: order != null,
          [getCssClassName(`alignSelf-${alignSelf}`)]: alignSelf != null,
          [getCssClassName(`overflow-y-${overflowY}`)]: overflowY != null,
          [getCssClassName(`overflow-x-${overflowX}`)]: overflowX != null,

          [css.inline]: inline,
          [css.collapsible]: collapsible,
        },
        getFlexContainerClassNames({
          justifyContent,
          flexDirection,
          flexWrap,
          gap,
          rowGap,
          columnGap,
          alignItems,
          alignContent,
        }),
        getDisplayClassNames({ display }),
      )}
      style={{
        ...style,
        width: generateSizeValue(width),
        height: generateSizeValue(height),
        flexBasis: generateSizeValue(flexBasis),
        maxWidth: generateSizeValue(maxWidth),
        minWidth: generateSizeValue(minWidth),
        maxHeight: generateSizeValue(maxHeight),
        minHeight: generateSizeValue(minHeight),
        overflow,
      }}
      ref={ref}
    />
  ),
);

Box.displayName = 'Box';

// Access the CSS module map using a string. Usually, we don't want to do this because we lose type
// safety. However, in this case, there are so many classes that accessing them statically would be
// unmanageable.
function getCssClassName(className: string): string {
  return css[className as ClassNames];
}

// Returns a formatted string representing a key in the CSS module map. If the value is null, null
// is returned instead.
function getSpaceClassName(
  type: 'm' | 'p',
  side: 't' | 'r' | 'b' | 'l',
  value: SpaceScale | undefined,
): string | null {
  return value == null ? null : `${type}${side}_${value}px`;
}

function generateSpaceClasses(
  type: 'm' | 'p',
  value: Partial<BoxStyleSpaceScale> | SpaceScale | undefined,
): Array<string | null> {
  if (value == null) return [];

  if (typeof value === 'number') return generateSpaceClasses(type, [value, value, value, value]);

  const [top, right, bottom, left] = value.length == 2 ? value.concat(value) : value;

  return [
    getSpaceClassName(type, 't', top),
    getSpaceClassName(type, 'r', right),
    getSpaceClassName(type, 'b', bottom),
    getSpaceClassName(type, 'l', left),
  ].map(c => c && getCssClassName(c));
}

// Returns a CSS object value. When passed a string, the input is assumed to already be a valid CSS
// value, and it is simply returned. Numbers above 1 are given the 'px' postfix, and numbers below 1
// are treated as percentages.
function generateSizeValue(value: undefined | number | string): undefined | string {
  if (typeof value === 'number') {
    if (value < 0) {
      throw new Error(`Size values cannot be negative. Received ${value}.`);
    } else if (value <= 1) {
      return `${(value * 100).toFixed(3)}%`;
    } else {
      return `${value}px`;
    }
  }

  return value;
}
